// Reusable Popover with Select All, optional Show Unthemed toggle, and scrollable checkbox list.
// Local selection mirrors parent state so the popover can be re-mounted with different initial selection.

import { useEffect, useState } from "react";
import {
    Button,
    Checkbox,
    List,
    ListItem,
    ListItemPrefix,
    Popover,
    PopoverContent,
    PopoverHandler,
    Typography,
} from "@material-tailwind/react";
import MaterialIcon from "components/material-icon";
import { identity, always } from "utils/utils";

export default function PopoverCheckboxes({
    items = [],
    isSelected = always(false),
    listItemContent = identity,
    buttonContent,
    onSubmit,
    itemAction,
    showUnthemed = false,
    unthemedChecked = false,
    onUnthemedApply = () => {},
    counter = null,
}) {
    const [openPopover, setOpenPopover] = useState(false);
    const [selected, setSelected] = useState(() => {
        return items.filter(isSelected);
    });
    const [unthemedSelected, setUnthemedSelected] = useState(unthemedChecked);

    useEffect(() => {
        setSelected(items.filter(isSelected));
    }, [items, isSelected]);

    useEffect(() => {
        setUnthemedSelected(unthemedChecked);
    }, [unthemedChecked]);

    const _isSelected = (x) => selected.includes(x);
    const allSelected = selected.length === items.length;
    const onAllCheckboxChange = () => setSelected(allSelected ? [] : items);

    const closePopover = () => setOpenPopover(false);

    const onCheckboxChange =
        (item) =>
            ({ target: { checked } }) => {
                setSelected((prev) =>
                    checked ? [...prev, item] : prev.filter((i) => i !== item),
                );
            };

    return (
        <Popover
            open={openPopover}
            placement="bottom-start"
            handler={setOpenPopover}
        >
            <PopoverHandler>
                <Button variant="outlined" className="w-full text-left px-3">
                    {buttonContent}
                    <MaterialIcon
                        className="float-right"
                        name="keyboard_arrow_down"
                        color="black"
                    />
                </Button>
            </PopoverHandler>
            <PopoverContent className="z-50 max-h-[50vh] max-w-[70vw] lg:max-w-[40vw] flex flex-col rounded-xl
                border-b border-platinumdark shadow-platinumdark">
                <label className="flex w-full items-center cursor-pointer pr-3">
                    <ListItemPrefix className="mr-0">
                        <Checkbox
                            onChange={onAllCheckboxChange}
                            checked={allSelected}
                        />
                    </ListItemPrefix>
                    <Typography>Select All</Typography>
                </label>
                {showUnthemed && (
                    <label className="flex w-full items-center cursor-pointer pr-3">
                        <ListItemPrefix className="mr-0">
                            <Checkbox
                                onChange={(e) => setUnthemedSelected(e.target.checked)}
                                checked={unthemedSelected}
                            />
                        </ListItemPrefix>
                        <Typography>Show Unthemed {counter?.unthemed_count !== undefined && `(${counter.unthemed_count})`}</Typography>
                    </label>
                )}
                <List className="p-0 flex-1 overflow-y-auto border-b border-t">
                    {items.map((item, index) => (
                        <ListItem
                            key={index}
                            className="p-0 flex-none overflow-hidden"
                        >
                            <div className="flex w-full items-center cursor-pointer">
                                <ListItemPrefix className="mr-0">
                                    <Checkbox
                                        onChange={onCheckboxChange(item)}
                                        checked={_isSelected(item)}
                                    />
                                </ListItemPrefix>
                                <div className="flex-1">
                                    {listItemContent(item)}
                                </div>
                                {itemAction && itemAction(item)}
                            </div>
                        </ListItem>
                    ))}
                </List>
                <div className="py-3 flex justify-center gap-2 pb-0">
                    <Button
                        onClick={() => {
                            onSubmit(selected);
                            onUnthemedApply(unthemedSelected);
                            closePopover();
                        }}
                        className="btn btn--primary"
                    >
                        Apply
                    </Button>
                    <Button
                        onClick={closePopover}
                        className="btn btn--primary"
                    >
                        Cancel
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
}
